import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  categoriesQuery,
  createAsset,
  productQuery,
  productsQuery,
  removeAsset,
  STATUS_LABELS,
  updateAsset,
  updateProduct,
  type Asset,
  type AssetChanges,
  type ProductChanges,
  type ProductDetail,
} from '../api/inventory.ts';
import { LINE_TYPE_LABELS, LINE_TYPES, type LineType } from '../api/agreements.ts';
import { rolesQuery } from '../api/crew.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { dollarsToMinorUnits, minorUnitsToDollars } from '../money.ts';

// Adding and correcting a unit, and correcting a product (CB-REQ INV-A01, INV-06).
//
// A correction sends only what changed. A field emptied is sent as null, which the API reads as
// "clear"; a field untouched is not sent at all, so saving a form never rewrites a value the person
// did not touch — and never asks for a permission they did not need, since cost fields are only
// sent when someone above the price line changed one.

type Money = { amountMinor: string; currency: 'AUD' };

/** The change to send for a text field: undefined when unchanged, null when emptied. */
export function textChange(
  original: string | null | undefined,
  now: string,
): string | null | undefined {
  const next = now.trim() || null;
  return next === (original ?? null) ? undefined : next;
}

/** Same for a whole-number field. Undefined result means unchanged; 'invalid' means refuse. */
export function numberChange(
  original: number | null | undefined,
  now: string,
): number | null | undefined | 'invalid' {
  const text = now.trim();
  if (!text) return original == null ? undefined : null;
  const n = Number(text);
  if (!Number.isInteger(n) || n <= 0) return 'invalid';
  return n === original ? undefined : n;
}

/** Same for money typed in dollars. */
export function moneyChange(
  original: { amountMinor: string } | null | undefined,
  now: string,
): Money | null | undefined | 'invalid' {
  if (!now.trim()) return original ? null : undefined;
  const minor = dollarsToMinorUnits(now);
  if (minor === null) return 'invalid';
  return minor === original?.amountMinor ? undefined : { amountMinor: minor, currency: 'AUD' };
}

/** Drops unchanged (undefined) keys so they are not sent. */
function onlyChanged<T extends Record<string, unknown>>(
  values: T,
): { [K in keyof T]?: Exclude<T[K], undefined> } {
  return Object.fromEntries(Object.entries(values).filter(([, v]) => v !== undefined)) as {
    [K in keyof T]?: Exclude<T[K], undefined>;
  };
}

function useRefresh(productId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: productQuery(productId).queryKey });
    await queryClient.invalidateQueries({ queryKey: productsQuery.queryKey });
  };
}

/** Adds a unit, or — given one — corrects it. */
export function AssetForm({
  productId,
  asset,
  seesCosts,
  onDone,
}: {
  productId: string;
  asset?: Asset;
  seesCosts: boolean;
  onDone: () => void;
}) {
  const refresh = useRefresh(productId);
  const editing = asset !== undefined;
  const [serialNumber, setSerialNumber] = useState(asset?.serialNumber ?? '');
  const [assetTag, setAssetTag] = useState(asset?.assetTag ?? '');
  const [status, setStatus] = useState<'in_stock' | 'in_repair' | 'quarantined'>('in_stock');
  const [purchasedOn, setPurchasedOn] = useState(asset?.purchasedOn ?? '');
  const [warrantyExpiresOn, setWarrantyExpiresOn] = useState(asset?.warrantyExpiresOn ?? '');
  const [purchasePrice, setPurchasePrice] = useState(
    asset?.purchasePrice ? minorUnitsToDollars(asset.purchasePrice.amountMinor) : '',
  );
  const [supplier, setSupplier] = useState(asset?.supplier ?? '');
  const [localError, setLocalError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const save = useMutation({
    mutationFn: async (changes: AssetChanges) =>
      editing
        ? updateAsset(asset.id, changes)
        : createAsset(productId, {
            ...(changes.serialNumber ? { serialNumber: changes.serialNumber } : {}),
            ...(changes.assetTag ? { assetTag: changes.assetTag } : {}),
            status,
            ...(changes.purchasedOn ? { purchasedOn: changes.purchasedOn } : {}),
            ...(changes.warrantyExpiresOn ? { warrantyExpiresOn: changes.warrantyExpiresOn } : {}),
            ...(changes.purchasePrice ? { purchasePrice: changes.purchasePrice } : {}),
            ...(changes.supplier ? { supplier: changes.supplier } : {}),
          }),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });

  const remove = useMutation({
    mutationFn: () => removeAsset(asset!.id),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);
    // INV-A01 at v1.941. The API and the database refuse it too; this saves the round trip.
    if (!serialNumber.trim() && !assetTag.trim()) {
      setLocalError('A unit needs a serial number, an asset tag, or both.');
      return;
    }
    const price = seesCosts ? moneyChange(asset?.purchasePrice, purchasePrice) : undefined;
    if (price === 'invalid') {
      setLocalError('Purchase price should be an amount in dollars, like 1299.50.');
      return;
    }
    const changes = onlyChanged({
      serialNumber: textChange(asset?.serialNumber, serialNumber),
      assetTag: textChange(asset?.assetTag, assetTag),
      purchasedOn: textChange(asset?.purchasedOn, purchasedOn),
      warrantyExpiresOn: textChange(asset?.warrantyExpiresOn, warrantyExpiresOn),
      purchasePrice: price,
      supplier: seesCosts ? textChange(asset?.supplier, supplier) : undefined,
    });
    if (editing && Object.keys(changes).length === 0) {
      onDone();
      return;
    }
    save.mutate(changes);
  }

  const failure = save.isError ? save.error : remove.isError ? remove.error : null;
  const error = localError ?? failure?.message ?? null;
  const busy = save.isPending || remove.isPending;
  const name = asset?.assetTag ?? asset?.serialNumber;

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4">
      <h3 className="section-title">{editing ? `Correct ${name}` : 'Add a unit'}</h3>
      {error ? (
        <p role="alert" className="alert">
          {error}
        </p>
      ) : null}
      <Field
        label="Asset tag"
        hint="Your own label, e.g. HPD082A. A serial number, an asset tag, or both."
        value={assetTag}
        onChange={(e) => setAssetTag(e.currentTarget.value)}
      />
      <Field
        label="Manufacturer serial number"
        value={serialNumber}
        onChange={(e) => setSerialNumber(e.currentTarget.value)}
      />
      {editing ? null : (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-ink">Condition now</legend>
          {(['in_stock', 'in_repair', 'quarantined'] as const).map((value) => (
            <label key={value} className="flex items-center gap-2 text-ink">
              <input
                type="radio"
                name="status"
                value={value}
                checked={status === value}
                onChange={() => setStatus(value)}
              />
              {STATUS_LABELS[value]}
            </label>
          ))}
        </fieldset>
      )}
      <Field
        label="Purchase date (optional)"
        type="date"
        value={purchasedOn}
        onChange={(e) => setPurchasedOn(e.currentTarget.value)}
      />
      <Field
        label="Warranty expires (optional)"
        type="date"
        value={warrantyExpiresOn}
        onChange={(e) => setWarrantyExpiresOn(e.currentTarget.value)}
      />
      {/* INV-A01: above the price line. Not offered to anyone the API would refuse it from. */}
      {seesCosts ? (
        <>
          <Field
            label="Purchase price, AUD, ex GST (optional)"
            inputMode="decimal"
            value={purchasePrice}
            onChange={(e) => setPurchasePrice(e.currentTarget.value)}
          />
          <Field
            label="Supplier (optional)"
            value={supplier}
            onChange={(e) => setSupplier(e.currentTarget.value)}
          />
        </>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone} disabled={busy}>
          Cancel
        </Button>
        {editing ? (
          // Two steps rather than a browser confirm: the second button says exactly what happens.
          confirmingRemove ? (
            <Button
              type="button"
              variant="quiet"
              className="ml-auto border-accent text-accent-700"
              onClick={() => remove.mutate()}
              disabled={busy}
            >
              {remove.isPending ? 'Removing…' : 'Delete'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="quiet"
              className="ml-auto"
              onClick={() => setConfirmingRemove(true)}
              disabled={busy}
            >
              Remove…
            </Button>
          )
        ) : null}
      </div>
      {editing ? (
        <p className="text-xs text-muted">
          Removing is for a unit recorded by mistake. A unit that was sold, scrapped or written off
          should be marked retired instead, which keeps it in the record.
        </p>
      ) : null}
    </form>
  );
}

/** Corrects a product. How it is counted is not editable (INV-A02), so it is shown, not offered. */
export function ProductForm({
  product,
  seesCosts,
  onDone,
}: {
  product: ProductDetail;
  seesCosts: boolean;
  onDone: () => void;
}) {
  const refresh = useRefresh(product.id);
  const categories = useQuery(categoriesQuery);
  const [name, setName] = useState(product.name);
  const [categoryId, setCategoryId] = useState(product.category?.id ?? '');
  const [manufacturer, setManufacturer] = useState(product.manufacturer ?? '');
  const [model, setModel] = useState(product.model ?? '');
  const [weight, setWeight] = useState(product.weightGrams?.toString() ?? '');
  const [length, setLength] = useState(product.lengthMm?.toString() ?? '');
  const [width, setWidth] = useState(product.widthMm?.toString() ?? '');
  const [height, setHeight] = useState(product.heightMm?.toString() ?? '');
  const [country, setCountry] = useState(product.countryOfOrigin ?? '');
  const [replacementCost, setReplacementCost] = useState(
    product.replacementCost ? minorUnitsToDollars(product.replacementCost.amountMinor) : '',
  );
  const [lineType, setLineType] = useState<LineType>(product.lineType);
  // OPS-01 (crew part 4A): a labour or transport line's task asks for this role.
  const crews = lineType === 'labour' || lineType === 'transport';
  const roles = useQuery({ ...rolesQuery, enabled: crews });
  const [crewRoleId, setCrewRoleId] = useState(product.crewRoleId ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (changes: ProductChanges) => updateProduct(product.id, changes),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);
    if (!name.trim()) {
      setLocalError('A product needs a name.');
      return;
    }
    const measures = {
      weightGrams: numberChange(product.weightGrams, weight),
      lengthMm: numberChange(product.lengthMm, length),
      widthMm: numberChange(product.widthMm, width),
      heightMm: numberChange(product.heightMm, height),
    };
    if (Object.values(measures).includes('invalid')) {
      setLocalError('Weight and dimensions are whole numbers of grams and millimetres.');
      return;
    }
    const countryText = country.trim().toUpperCase();
    if (countryText && !/^[A-Z]{2}$/.test(countryText)) {
      setLocalError('Country of origin is a two-letter code, like AU or CN.');
      return;
    }
    const cost =
      seesCosts && !product.ownerVenueId
        ? moneyChange(product.replacementCost, replacementCost)
        : undefined;
    if (cost === 'invalid') {
      setLocalError('Replacement cost should be an amount in dollars, like 1299.50.');
      return;
    }
    const changes = onlyChanged({
      name: name.trim() === product.name ? undefined : name.trim(),
      categoryId:
        (categoryId || null) === (product.category?.id ?? null) ? undefined : categoryId || null,
      manufacturer: textChange(product.manufacturer, manufacturer),
      model: textChange(product.model, model),
      ...(measures as Record<string, number | null | undefined>),
      countryOfOrigin: textChange(product.countryOfOrigin, countryText),
      replacementCost: cost,
      lineType: lineType === product.lineType ? undefined : lineType,
      crewRoleId:
        (crewRoleId || null) === (product.crewRoleId ?? null) ? undefined : crewRoleId || null,
    });
    if (Object.keys(changes).length === 0) {
      onDone();
      return;
    }
    save.mutate(changes as ProductChanges);
  }

  const error = localError ?? (save.isError ? save.error.message : null);

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4">
      <h2 className="section-title">Correct this product</h2>
      {error ? (
        <p role="alert" className="alert">
          {error}
        </p>
      ) : null}
      <Field label="Name" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
      <p className="text-sm text-ink-soft">
        Counted by {product.tracking === 'serialised' ? 'serial or tag' : 'quantity'}. This is fixed
        once a product is saved; a product set up the wrong way is replaced by a new one.
      </p>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="edit-category" className="text-xs text-ink-soft">
          Category
        </label>
        <select
          id="edit-category"
          value={categoryId}
          onChange={(e) => setCategoryId(e.currentTarget.value)}
          className="input"
        >
          <option value="">None</option>
          {(categories.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      {/* FIN-17 at v1.988: a venue's margin is set per line type. */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="edit-line-type" className="text-xs text-ink-soft">
          Line type
        </label>
        <select
          id="edit-line-type"
          value={lineType}
          onChange={(e) => setLineType(e.currentTarget.value as LineType)}
          className="input"
        >
          {LINE_TYPES.map((t) => (
            <option key={t} value={t}>
              {LINE_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </div>
      {crews ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="edit-crew-role" className="text-xs text-ink-soft">
            Crew role
          </label>
          <select
            id="edit-crew-role"
            value={crewRoleId}
            onChange={(e) => setCrewRoleId(e.currentTarget.value)}
            className="input"
          >
            <option value="">None</option>
            {(roles.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Manufacturer"
          value={manufacturer}
          onChange={(e) => setManufacturer(e.currentTarget.value)}
        />
        <Field label="Model" value={model} onChange={(e) => setModel(e.currentTarget.value)} />
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <Field
          label="Weight, g"
          inputMode="numeric"
          value={weight}
          onChange={(e) => setWeight(e.currentTarget.value)}
        />
        <Field
          label="Length, mm"
          inputMode="numeric"
          value={length}
          onChange={(e) => setLength(e.currentTarget.value)}
        />
        <Field
          label="Width, mm"
          inputMode="numeric"
          value={width}
          onChange={(e) => setWidth(e.currentTarget.value)}
        />
        <Field
          label="Height, mm"
          inputMode="numeric"
          value={height}
          onChange={(e) => setHeight(e.currentTarget.value)}
        />
      </div>
      <Field
        label="Country of origin"
        hint="Two letters, e.g. AU, CN, US."
        maxLength={2}
        value={country}
        onChange={(e) => setCountry(e.currentTarget.value)}
      />
      {/* INV-A14 at v1.982: a venue's own equipment is not the company's to value. */}
      {seesCosts && !product.ownerVenueId ? (
        <Field
          label="Replacement cost, AUD"
          inputMode="decimal"
          value={replacementCost}
          onChange={(e) => setReplacementCost(e.currentTarget.value)}
        />
      ) : null}
      <div className="flex gap-3">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
